import styled from '@emotion/styled'
import React, { ComponentProps, useEffect } from 'react'
import Helmet from 'react-helmet'

import { pushPageView } from '@emico/datalayer'
import Header from '@emico/header'
import { PortalTarget } from '@emico/portal'
import { overrideOptional } from '@emico/utils'

import Footer from './Footer'

export type PageType =
  | 'cms'
  | 'product'
  | 'category'
  | 'user'
  | 'cart'
  | 'checkout'
  | 'search'
  | 'faq'
  | 'N/A'

const Page = styled.div`
  display: flex;

  /* Make the page at least full height with the footer within view if the page isn't long enough */
  min-height: 100vh;
  flex-direction: column;
  text-rendering: optimizeLegibility;
`

export const PageWrapperContent = overrideOptional(
  styled('main', {
    shouldForwardProp: (prop) => prop !== 'hasPadding',
  })<{ hasPadding?: boolean }>`
    position: relative;
    flex: 1 1 auto;
    padding: ${(props) => (props.hasPadding ? '40px 0' : '0')};
  `,
)

interface Props extends ComponentProps<typeof Page> {
  pageType: PageType
  pageCategory?: string
  // A custom header if you want to override the default. Pass `null` to disable the header completely.
  header?: React.ReactNode
  // A custom footer if you want to override the default. Pass `null` to disable the footer completely.
  footer?: React.ReactNode
  headTitle?: string
  children: React.ReactNode
  className?: string
  contentClassName?: string
  padding?: boolean
}

/**
 * The pageWrapper class and pageType classes are used as hooks within the application
 * to change styling based on page type.
 */
const PageWrapper = ({
  pageType,
  pageCategory,
  children,
  headTitle,
  header,
  footer,
  padding = true,
  contentClassName,
  ...others
}: Props) => {
  useEffect(() => {
    pushPageView(pageType, pageCategory)
  }, [pageCategory, pageType])
  return (
    <PortalTarget>
      <Page {...others}>
        {headTitle && (
          <Helmet>
            <title>{headTitle}</title>
          </Helmet>
        )}

        {header !== undefined ? header : <Header />}

        <PageWrapperContent
          className={contentClassName}
          hasPadding={Boolean(padding)}
        >
          {children}
        </PageWrapperContent>

        {footer !== undefined ? footer : <Footer />}
      </Page>
    </PortalTarget>
  )
}

export default overrideOptional(PageWrapper)
